import React, { ChangeEvent, Fragment, ReactElement, useState } from "react";
import { BaseType, IColumn, ILRFProduction, IParentSize, Order } from "../../../../types";
import {
  formatDate,
  formatDateTimeWithSeconds,
  getComparator,
  stableSort
} from "../../../../system/utils";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead, TablePagination,
  TableRow,
  TableSortLabel, Tooltip
} from "@mui/material";
import StyledTableRow from "../../../../components/styledTableRow";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Detail from "../../../../components/dialogs/detail";
import LRFHeatInformation from "../lrfHeatInformation";
import TableContainer from "../../../../components/tableContainer";
import TablePaginationActions from "../../../../components/tablePaginationActions";

interface IProps {
  productionData: ILRFProduction[];
  parentSize: IParentSize;
}

interface IDetailDialog {
  open: boolean;
  item: ILRFProduction | null;
}

const columns: IColumn[] = [
  {
    id: "detail",
    label: "",
    minWidth: 80,
  },
  {
    id: "heatNumber",
    label: "Heat Number",
    minWidth: 80,
  },
  {
    id: "sequence",
    label: "Sequence",
    minWidth: 80,
  },
  {
    id: "factoryDay",
    label: "Factory Day",
    minWidth: 80,
    format: value => formatDate(value),
  },
  {
    id: "shiftName",
    label: "Shift",
    minWidth: 80,
  },
  {
    id: "crew",
    label: "Crew",
    minWidth: 80
  },
  {
    id: "steel",
    label: "Steel",
    minWidth: 80,
  },
  {
    id: "beginDatetime",
    label: "Begin Datetime",
    minWidth: 170,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "endDatetime",
    label: "End Datetime",
    minWidth: 170,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "ladle",
    label: "Ladle",
    minWidth: 80,
  },
  {
    id: "lastTemp",
    label: <span>Last Temp <sub>[°C]</sub></span>,
    minWidth: 80,
    align: "right",
    format: (value) => value.toFixed(0)
  },

  {
    id: "lastO2PPM",
    label: "last O2PPM",
    minWidth: 80,
    align: "right",
    format: (value) => value.toFixed(2)
  }
];

const ProductionTable = ({ productionData, parentSize }: IProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof ILRFProduction>("beginDatetime");
  const [selectedItem, setSelectedItem] = useState<ILRFProduction | null>(null);
  const [openDetail, setOpenDetail] = useState<IDetailDialog>({ open: false, item: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const tableContainerHeight = parentSize.height - 50;

  const dataLength = productionData ? productionData.length : 0;

  const createSortHandler = (property: keyof ILRFProduction) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="flex-grow-1">
      <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
        <Table stickyHeader={true}  size={"small"}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) =>
                <TableCell
                  sx={{ backgroundColor: "#b0bec5" }}
                  key={`${column.id}-${index}`}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sortDirection={orderBy === column.id ? order : false}>
                  {column.id !== "actions"
                    ? <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={createSortHandler(column.id as keyof ILRFProduction)}
                    >
                      {column.label}
                      {orderBy === column.id
                        ? <span className="cell-visuallyHidden">
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </span>
                        : null}
                    </TableSortLabel>
                    : <Fragment />}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {productionData && stableSort<ILRFProduction>(productionData, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: ILRFProduction, index: number) => {
                return (
                  <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`prod-eaf-row-${index}`}
                    onClick={() => setSelectedItem(row)} selected={row === selectedItem}>
                    {columns.map((column: IColumn) => {

                      if(column.id === "detail") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align}>
                            <IconButton aria-label="edit" size={"small"}  onClick={() => setOpenDetail({ open: true, item: row })}>
                              <Tooltip title="Show Detail">
                                <OpenInNewIcon/>
                              </Tooltip>
                            </IconButton>
                          </TableCell>
                        );
                      }

                      if(column.id === "factoryDay") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align}>
                            {column.format ? column.format(row.shiftHistory.factoryDay) : ""}
                          </TableCell>
                        );
                      }

                      if(column.id === "shiftName") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align}>
                            {row.shiftHistory.name}
                          </TableCell>
                        );
                      }

                      if(column.id === "crew") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align}>
                            {row.crew.name}
                          </TableCell>
                        );
                      }

                      if(column.id === "steel") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align}>
                            {row.steel.code}
                          </TableCell>
                        );
                      }

                      if(column.id === "ladle") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align}>
                            {row.ladleHistory && row.ladleHistory.ladle}
                          </TableCell>
                        );
                      }

                      const value = row[column.id as keyof ILRFProduction] as BaseType;

                      return (
                        <TableCell key={`${column.id}-${index}`} align={column.align}>
                          {column.format && typeof value === "number" ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
        {!productionData &&
        <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2"  >
          <CircularProgress className=""/>
        </Paper>
        }
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
              colSpan={columns.length + 1}
              count={dataLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableBody>
      </Table>
      {openDetail.open && openDetail.item &&
      <Detail closed={() => setOpenDetail({ open: false, item: null })} fullWidth={true} maxWidth={"xl"}
        title={`Detail - ${openDetail.item.machine.name} - ${openDetail.item.heatNumber}`}>
        <LRFHeatInformation productionItem={ openDetail.item} height={500}/>
      </Detail>
      }
    </div>
  );
};

export default ProductionTable;
